import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { subscribeToGame, sendGameAction, submitGameLog } from '../services/gameService';
import { useAppContext } from '../AppContext';

// Types for clarity (converted from TS interface)
const ShowdownStatus = {
  WAITING: 'WAITING',
  ACTIVE: 'ACTIVE',
  FINISHED: 'FINISHED'
};

const NexusDreamWeaveShowdown = ({
  nexusId,
  currentUser,
  rivalId = null,
  onClose,
  challengeId = null // NEW PROP
}) => {
  // -- Context --
  const { t } = useAppContext(); // Assuming t is available in AppContext, else import useTranslation

  // -- State --
  // Mock initial state to allow render before Firestore connects
  const [gameState, setGameState] = useState({
      status: ShowdownStatus.WAITING,
      balance: 0,
      timeRemaining: 30,
      aiState: 'Initializing connection...',
      gameId: 'pending'
  });
  const [optimisticBalance, setOptimisticBalance] = useState(0);
  const [soulsGained, setSoulsGained] = useState(0);
  const [floatingTexts, setFloatingTexts] = useState([]);
  
  // -- Refs for Logic --
  const gameLog = useRef([]); // High-frequency log
  const lastVibrate = useRef(0);
  const floatIdCounter = useRef(0);
  const controls = useAnimationControls();

  // -- Effects --

  // 1. Subscription & Game Loop
  useEffect(() => {
    if (!nexusId && !challengeId) return;

    // Determine ID and flag
    const targetId = challengeId || nexusId;
    const isChallenge = !!challengeId;

    const unsubscribe = subscribeToGame(targetId, (newState) => {
      if (newState) {
        setGameState(newState);
        
        // Sync optimistic balance slowly towards server balance to prevent jumps,
        // but allow local clicks to override momentarily.
        if (Math.abs(newState.balance - optimisticBalance) > 10) {
          setOptimisticBalance(newState.balance);
        }
      }
    }, isChallenge);

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nexusId, challengeId]);

  // 2. Sync Optimistic State with Game State periodically
  useEffect(() => {
    if (gameState) {
        // A simple damping factor to pull optimistic state to truth
        const timer = setInterval(() => {
            setOptimisticBalance(prev => {
                const diff = gameState.balance - prev;
                // If diff is small, snap to it to avoid infinite micro-adjustments
                if (Math.abs(diff) < 0.5) return gameState.balance;
                return prev + (diff * 0.1); 
            });
        }, 50);
        return () => clearInterval(timer);
    }
  }, [gameState]);

  // -- Handlers --

  const triggerHaptic = (pattern) => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      const now = Date.now();
      if (now - lastVibrate.current > 100) { // Debounce haptics
        navigator.vibrate(pattern);
        lastVibrate.current = now;
      }
    }
  };

  const addFloatingText = (text, x, y) => {
    const id = floatIdCounter.current++;
    setFloatingTexts(prev => [...prev, { id, text, x, y }]);
    setTimeout(() => {
      setFloatingTexts(prev => prev.filter(ft => ft.id !== id));
    }, 1000);
  };

  const handleChannel = useCallback((e) => {
    // Allow interaction if active OR if we are in a 'demo' waiting mode (optional)
    if (!gameState || gameState.status !== ShowdownStatus.ACTIVE) return;

    // Prevent default behavior for touch to avoid scrolling/zooming
    // if (e.cancelable) e.preventDefault(); // Note: Might block click events if not careful

    // 1. Calculate Impact (Critical Click?)
    const isCritical = Math.random() > 0.9;
    const power = isCritical ? 5 : 2;

    // 2. Optimistic UI Update
    setOptimisticBalance(prev => Math.min(100, Math.max(-100, prev + power)));
    
    // 3. Visual Feedback
    if (isCritical) {
      triggerHaptic([10, 50, 10]);
      setSoulsGained(prev => prev + 5);
      // Add visual popup at random position near center
      addFloatingText("CRITICAL!", window.innerWidth / 2 + (Math.random() * 100 - 50), window.innerHeight / 2);
    } else {
      triggerHaptic([5]); // Light tap
    }

    // 4. Animate UI
    controls.start({
      scale: [1, 0.95, 1],
      transition: { duration: 0.1 }
    });

    // 5. Log Data for AI Training
    gameLog.current.push({
      timestamp: Date.now(),
      action: 'CHANNEL',
      value: power,
      currentBalance: gameState.balance
    });

    // 6. Send to Backend
    const targetId = challengeId || nexusId;
    const isChallenge = !!challengeId;
    sendGameAction(targetId, power, isChallenge);

  }, [gameState, controls, nexusId, challengeId]);

  // -- Render Helpers --

  if (!gameState) return <div className="flex items-center justify-center h-full bg-gray-900 text-cyan-400">Connecting to Nexus...</div>;

  const balancePercent = ((optimisticBalance + 100) / 200) * 100; // Convert -100..100 to 0..100%
  const isWinning = optimisticBalance > 0;

  // -- Results Screen --
  if (gameState.status === ShowdownStatus.FINISHED) {
    // Final calculation
    const winner = gameState.balance > 0 ? 'VICTORY' : 'DEFEAT';
    const totalSouls = soulsGained + (winner === 'VICTORY' ? 50 : 10);
    
    // Flush logs on mount of result screen
    if (gameLog.current.length > 0) {
       // Note: submitGameLog uses nexusId for file path construction, so we pass nexusId still
       // If challengeId is present, maybe we want logs there? For now, keep logs in Nexus for training
       submitGameLog(nexusId, gameState.gameId, gameLog.current);
       gameLog.current = []; // Clear to prevent double submit
    }

    return (
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-gray-900/95 backdrop-blur-xl p-8 text-center"
      >
        <LucideIcons.Trophy className={`w-24 h-24 mb-6 ${winner === 'VICTORY' ? 'text-yellow-400' : 'text-gray-500'}`} />
        <h1 className="text-4xl font-black tracking-wider mb-2 text-white">{winner}</h1>
        <p className="text-purple-400 mb-8">The DreamWeave has observed.</p>
        
        <div className="bg-gray-800 p-6 rounded-2xl w-full max-w-md mb-8 border border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <span className="text-gray-400">Souls Harvested</span>
            <span className="text-2xl font-bold text-cyan-400">+{totalSouls}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Contribution</span>
            <span className="text-xl font-bold text-white">S-Rank</span>
          </div>
        </div>

        <button 
          onClick={onClose}
          className="w-full max-w-md py-4 bg-cyan-600 text-white font-bold rounded-full hover:bg-cyan-500 transition-colors"
        >
          Return to Nexus
        </button>
      </motion.div>
    );
  }

  return (
    <div className="relative h-full w-full flex flex-col bg-gray-900 overflow-hidden select-none rounded-xl">
      
      {/* --- Top Section: The Visualizer (Battlefield) --- */}
      <div className="relative flex-1 flex flex-col items-center justify-center p-4">
        
        {/* Background Ambient Glow */}
        <div className={`absolute inset-0 opacity-20 transition-colors duration-1000 ${isWinning ? 'bg-blue-500' : 'bg-red-900'}`} />
        
        {/* Opponent Avatar */}
        <div className="absolute top-4 flex flex-col items-center z-10">
           <div className="w-16 h-16 rounded-full border-4 border-red-500 bg-black flex items-center justify-center shadow-[0_0_20px_rgba(255,65,108,0.6)]">
             {rivalId ? <span className="text-xl text-white font-bold">VS</span> : <LucideIcons.Cpu className="w-8 h-8 text-white animate-pulse" />}
           </div>
           <div className="mt-2 px-3 py-1 bg-black/40 backdrop-blur rounded-full border border-white/10">
             <span className="text-xs font-bold text-red-400 tracking-widest uppercase">
               {rivalId ? 'Rival Nexus' : 'DreamWeave AI'}
             </span>
           </div>
           {/* AI Status Text */}
           {!rivalId && (
             <motion.p 
                key={gameState.aiState}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs text-red-300 mt-1 font-mono"
             >
               [{gameState.aiState}]
             </motion.p>
           )}
        </div>

        {/* The Balance Bar (Tug of War) */}
        <div className="relative w-16 h-3/4 max-w-md flex items-center justify-center">
          {/* Track */}
          <div className="absolute w-4 bg-white/10 h-full rounded-full overflow-hidden border border-white/5">
            <div className="absolute top-0 left-0 w-full bg-gradient-to-b from-red-600 to-red-900" style={{ height: '50%' }} />
            <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-cyan-500 to-blue-600" style={{ height: '50%' }} />
          </div>

          {/* The Orb (Current Balance) */}
          <motion.div 
            className="absolute w-12 h-12 rounded-full shadow-xl z-20 flex items-center justify-center border-2 border-white"
            style={{ 
              top: `${100 - balancePercent}%`, // Inverted because 100% is "Us" (Bottom)
              backgroundColor: isWinning ? '#00d2ff' : '#ff416c',
              boxShadow: isWinning ? '0 0 30px #00d2ff' : '0 0 30px #ff416c'
            }}
          >
             <LucideIcons.Activity className="w-6 h-6 text-white" />
          </motion.div>
          
          {/* Particle/Energy Beam Effects */}
          <div 
            className="absolute w-1 bg-gradient-to-b from-red-500 to-cyan-500 h-full opacity-50 blur-md" 
          />
        </div>

        {/* Score Display */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex flex-col gap-8 text-white/50 font-mono text-sm">
           <span className="text-red-400 font-bold">{Math.round(50 - (optimisticBalance/2))}%</span>
        </div>
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-8 text-cyan-400 font-mono text-sm text-right">
           <span className="font-bold">{Math.round(50 + (optimisticBalance/2))}%</span>
        </div>
      </div>

      {/* --- Floating Text Overlay --- */}
      <AnimatePresence>
        {floatingTexts.map(ft => (
          <motion.div
            key={ft.id}
            initial={{ opacity: 1, y: ft.y, x: ft.x, scale: 0.5 }}
            animate={{ opacity: 0, y: ft.y - 100, scale: 1.5 }}
            exit={{ opacity: 0 }}
            className="absolute pointer-events-none text-yellow-400 font-black text-2xl z-50 drop-shadow-md"
          >
            {ft.text}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* --- Bottom Section: Controls --- */}
      <div className="bg-gray-800/90 backdrop-blur-lg border-t border-white/10 p-6 pb-10 flex flex-col items-center rounded-t-3xl z-30 shadow-[0_-10px_40px_rgba(0,0,0,0.5)]">
        
        {/* Info Bar */}
        <div className="flex justify-between w-full max-w-md mb-6 text-sm font-bold px-4">
          <div className="flex items-center gap-2 text-purple-400">
             <LucideIcons.Zap className="w-4 h-4" />
             <span>{currentUser?.souls ? currentUser.souls + soulsGained : soulsGained} Souls</span>
          </div>
          <div className={`flex items-center gap-2 ${gameState.timeRemaining < 10 ? 'text-red-500 animate-pulse' : 'text-white'}`}>
             <span>{Math.ceil(gameState.timeRemaining)}s Remaining</span>
          </div>
        </div>

        {/* The Channel Button */}
        <motion.button
          animate={controls}
          onMouseDown={handleChannel}
          onTouchStart={handleChannel} // Mobile Optimised
          className="relative w-24 h-24 md:w-32 md:h-32 rounded-full group focus:outline-none"
        >
          {/* Button Glows */}
          <div className="absolute inset-0 bg-cyan-500 rounded-full blur-lg opacity-40 group-active:opacity-80 transition-opacity duration-100" />
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-full opacity-90 group-active:scale-95 transition-transform duration-100 border-4 border-white/20 flex items-center justify-center overflow-hidden">
             <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/stardust.png")' }} />
             <LucideIcons.Smartphone className="w-10 h-10 text-white group-active:scale-125 transition-transform" />
          </div>
          
          {/* Ripple Rings */}
          <div className="absolute inset-[-10px] border border-cyan-400/30 rounded-full animate-ping" />
          <div className="absolute inset-[-20px] border border-cyan-400/10 rounded-full animate-pulse" />
        </motion.button>
        
        <p className="mt-6 text-white/40 text-xs uppercase tracking-[0.2em] animate-pulse">
          Tap rapidly to synchronize
        </p>

      </div>
    </div>
  );
};

export default NexusDreamWeaveShowdown;
